import { thunkGetAppConfig } from 'app/appSlice';
import SystemHelper, { SystemConstants } from 'features/System/systemHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DialogAddStaffView from '../DialogAddStaffView';
import AppConstants from 'general/constants/AppConstants';

StaffListView.propTypes = {
  showFullUI: PropTypes.bool,
};

const defaultColumnIds = ['AVATAR', 'STAFF_CODE', 'NAME', 'EMAIL', 'PHONE', 'PERMISSION', 'STATUS'];

function StaffListView({ showFullUI }) {
  // MARK: ---- PROPERTIES ----
  const dispatch = useDispatch();
  const config = useSelector((state) => state.app?.appConfig?.config) ?? [];
  const poaUserId = _.find(config, (item) => item.key === 'poaUserId')?.value;
  const router = useRouter();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [filter, setFilter] = useState(SystemHelper.Filter.staffList);
  const [staffList, setStaffList] = useState([]);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    let params = _.pickBy(filter, (value) => value !== null && value !== undefined && value !== '');
    params.role = user?.role;
    SystemHelper.API.User.requestGetUserList(params)
      .then((response) => {
        setStaffList(response.users);
        setTotal(response.total);
      })
      .finally(() => { });
  }, [filter, user]);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'AVATAR',
        name: UIHelper.buildTableColumn('Avatar'),
        selector: (row) =>
          UIHelper.buildAvatar(
            row?.avatar ? Utils.getFullUrl(row?.avatar) : null,
            40,
            AppResource.icons.avatar.default,
            false,
            false
          ),
        width: '70px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STAFF_CODE',
        name: UIHelper.buildTableColumn('Mã nhân viên'),
        selector: (row) => (
          <span className="d-flex align-items-center gap-2">
            {row?.userCode ?? row?.userId}
            {row?.userId == poaUserId && (
              <KTTooltip text="Đây là tài khoản uỷ quyền">
                <i className="fa-solid fa-user-check" style={{ color: AppColors.primary }} />
              </KTTooltip>
            )}
          </span>
        ),
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'NAME',
        name: UIHelper.buildTableColumn('Họ và tên'),
        selector: (row) => row?.fullname,
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'EMAIL',
        name: UIHelper.buildTableColumn('Email'),
        selector: (row) => row?.email,
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PHONE',
        name: UIHelper.buildTableColumn('Số điện thoại'),
        selector: (row) => row?.phone,
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PERMISSION',
        name: UIHelper.buildTableColumn('Nhóm quyền'),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60">{row?.permissionGroup?.name}</span>
        ),
        minWidth: '160px',
        style: tableStyle.columns.link,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái'),
        selector: (row) => {
          return (
            <KTLabel
              text={SystemHelper.Utils.User.getUserStatusName(row?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${SystemHelper.Utils.User.getUserStatusColor(
                row?.status
              )}`}
            />
          );
        },
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        maxWidth: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
        },
      },
    ];
  }, [poaUserId]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  // MARK: ---- METHODS ----
  const buildMoreActions = (row) => {
    const actions = [];
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.staffUpdate.code)) {
      // chinh sua
      actions.push({
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          handleAddNew(row);
        },
      });
      if (row?.status === SystemConstants.UserStatus.NORMAL) {
        // ngung hoat dong
        actions.push({
          text: 'Ngừng hoạt động',
          iconElement: <i className="fa-regular fa-stop" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            SystemHelper.UI.User.showDialogConfirmSuspendUser(row, () => {
              handleRefresh();
            });
          },
        });
      }
      if (row?.status === SystemConstants.UserStatus.SUSPENDED) {
        // khoi phuc hoat dong
        actions.push({
          text: 'Khôi phục hoạt động',
          iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            SystemHelper.UI.User.showDialogConfirmResumeUser(row, () => {
              handleRefresh();
            });
          },
        });
      }
      if (poaUserId == row?.userId) {
        // huy uy quyen
        actions.push({
          text: 'Hủy uỷ quyền',
          iconElement: <i className="fa-regular fa-user-xmark" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            SystemHelper.UI.User.showDialogConfirmUnsetPoaUser(row, () => {
              dispatch(thunkGetAppConfig());
              handleRefresh();
            });
          },
        });
      } else {
        if (row?.status !== SystemConstants.UserStatus.REGISTERED) {
          // gan uy quyen
          actions.push({
            text: 'Gán uỷ quyền',
            iconElement: <i className="fa-regular fa-user-check" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              SystemHelper.UI.User.showDialogConfirmSetPoaUser(row, () => {
                dispatch(thunkGetAppConfig());
                handleRefresh();
              });
            },
          });
        }
      }
    }
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.staffDelete.code)) {
      // xoa
      actions.push({
        text: 'Xoá',
        iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          SystemHelper.UI.User.showDialogConfirmDeleteUser(row, () => {
            handleRefresh();
          });
        },
      });
    }
    return actions;
  };
  const handleRefresh = () => {
    setFilter({ ...SystemHelper.Filter.staffList });
  };
  const handleDelete = (row) => { };
  const handleAddNew = (row) => {
    AppCustomDialogHelper.show(
      !!row ? 'Cập nhật nhân viên' : 'Thêm mới nhân viên',
      <DialogAddStaffView
        initialRole={user?.role}
        item={row}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          AppCustomDialogHelper.hide();
          handleRefresh();
        }}
      />,
      'lg',
      false,
      true
    );
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div>
          <div className="pb-4">
            {/* header */}
            <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
              {/* title */}
              <div className="d-flex flex-column">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Danh sách nhân viên
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                    {Utils.formatNumber(total)} nhân viên
                  </span>
                </span>
              </div>

              {/* actions */}
              {Utils.checkPermission(permissionList, AppConstants.permissionCode.staffCreate.code) &&
                UIHelper.buildGreenButton(
                  'Thêm nhân viên',
                  handleAddNew,
                  <i className="fa-regular fa-user-plus" />
                )}
            </div>

            {/* table */}
            <div>
              {/* filter */}
              <div className="row m-0 p-0 px-2">
                {/* tim kiem */}
                <div className="col-xl-3 col-md-4 col-6 mb-4 px-2">
                  {UIHelper.buildFormLabel('Tìm kiếm')}
                  <KTFormSearch
                    name="search"
                    placeholder="Theo mã nhân viên, họ và tên, email, số điện thoại ... ."
                    value={filter.q}
                    onSubmit={(text) => {
                      SystemHelper.Filter.staffList = {
                        ...SystemHelper.Filter.staffList,
                        q: text,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                  />
                </div>
                {/* loc theo trang thai */}
                <div className="col-xl-3 col-md-4 col-6 mb-4 px-2">
                  {UIHelper.buildFormLabel('Trạng thái')}
                  <KTBSDropdown
                    name="status"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(
                          SystemHelper.Data.UserStatusOptions,
                          (item) => item.value === SystemHelper.Filter.staffList?.status
                        )?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={SystemHelper.Filter.staffList?.status}
                    dropdownMenuItems={[
                      { name: 'Tất cả', value: '' },
                      ...SystemHelper.Data.UserStatusOptions,
                    ]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      SystemHelper.Filter.staffList = {
                        ...SystemHelper.Filter.staffList,
                        status: value,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                    isDropdownForm={true}
                  />
                </div>
                {/* thiet lap cot */}
                <div className="col-xl-3 col-md-4 col-6 mb-4 px-2 d-flex align-items-end gap-3">
                  <DropdownTableConfigColumn
                    columnIds={columnIds}
                    fullColumns={fullColumns}
                    fullColumnIds={fullColumnsIdSorted}
                    onChangeCheckboxValue={onChangeCheckboxValue}
                    resetDefaultTableColumn={resetDefaultTableColumn}
                    onChangeFullColumnIds={(ids) => {
                      setFullColumnIds(ids);
                      const key = prefKey + '_FULL_COLUMN_IDS';
                      localStorage.setItem(key, ids.join('|'));
                    }}
                    onSelectAll={onSelectAll}
                    onUnSelectAll={onUnSelectAll}
                    saveChanges={saveChanges}
                  />
                </div>
              </div>
              <div className="px-4">
                <KTTable
                  columns={columns}
                  items={staffList}
                  selectableRows={false}
                  striped={false}
                  compactGrid={false}
                  onClickedRow={(row) => { }}
                  onSelectedRowsChanged={(rows) => { }}
                  onDoubleClickedRow={(row) => { }}
                  noDataComponent={UIHelper.buildEmptyNoData(
                    'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                  )}
                />
              </div>
            </div>

            {/* pagination */}
            <div className="px-4 mt-2">
              <KTPagination
                currentPage={filter.page + 1}
                rowCount={total}
                rowsPerPage={filter.limit}
                currentItemInPageCount={staffList?.length}
                onChangePage={(newPage) => {
                  const iNewPage = parseInt(newPage);
                  SystemHelper.Filter.staffList = {
                    ...SystemHelper.Filter.staffList,
                    page: iNewPage - 1,
                  };
                  handleRefresh();
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                  const iNewRowsPerPage = parseInt(newRowsPerPage);
                  SystemHelper.Filter.staffList = {
                    ...SystemHelper.Filter.staffList,
                    limit: iNewRowsPerPage,
                    page: 0,
                  };
                  handleRefresh();
                }}
                loading={false}
                color={KTPaginationColor.success}
                appendText="nhân viên"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffListView;
